
import { logger } from '@/utils/production-logger';
import { ConnectionState } from './types';

export class ConnectionManager {
  private static activeConnections = new Map<string, ConnectionState>();
  
  static initializeConnection(requestId: string): AbortController {
    // Cleanup any existing connection
    this.cleanup(requestId);

    const abortController = new AbortController();
    this.activeConnections.set(requestId, {
      status: 'connecting',
      lastActivity: Date.now(),
      abortController,
      requestId,
      startEventFired: false // STEP 1: Keep tracking but don't block events
    });

    console.log('[CONNECTION MANAGER STREAM FIX] ✅ Connection initialized:', {
      requestId,
      status: 'connecting'
    });

    return abortController;
  }

  static updateConnectionStatus(requestId: string, status: ConnectionState['status']): void {
    const connection = this.activeConnections.get(requestId);
    if (connection) {
      connection.status = status;
      connection.lastActivity = Date.now();
      
      console.log('[CONNECTION MANAGER STREAM FIX] 📊 Status updated:', {
        requestId,
        status,
        lastActivity: new Date(connection.lastActivity).toISOString()
      });
    }
  }

  static markStartEventFired(requestId: string): void {
    const connection = this.activeConnections.get(requestId);
    if (connection) {
      connection.startEventFired = true;
      console.log('[CONNECTION MANAGER STREAM FIX] 🚀 Start event marked as fired:', {
        requestId
      });
    }
  }

  // STEP 1: Modified to allow duplicate starts for proper streaming
  static hasStartEventFired(requestId: string): boolean {
    const connection = this.activeConnections.get(requestId);
    const hasFired = connection?.startEventFired || false;
    
    console.log('[CONNECTION MANAGER STREAM FIX] 🔍 Checking start event status:', {
      requestId,
      hasFired,
      willAllowDuplicate: false // We track but don't block
    });
    
    return hasFired;
  }

  // STEP 1: Allow start events to proceed for proper streaming initialization
  static shouldAllowStartEvent(requestId: string): boolean {
    const connection = this.activeConnections.get(requestId);
    
    console.log('[CONNECTION MANAGER STREAM FIX] ✅ Allowing start event (no blocking):', {
      requestId,
      hasConnection: !!connection,
      status: connection?.status
    });
    
    return true; // Always allow start events for proper streaming
  }

  static cleanup(requestId: string): void {
    const connection = this.activeConnections.get(requestId);
    if (connection) {
      connection.abortController.abort();
      this.activeConnections.delete(requestId);
      
      console.log('[CONNECTION MANAGER STREAM FIX] 🧹 Connection cleanup completed:', {
        requestId
      });
      
      logger.debug('Connection cleanup completed', {
        requestId,
        component: 'ConnectionManager'
      });
    }
  }

  static stopAll(): void {
    console.log('[CONNECTION MANAGER STREAM FIX] 🛑 Stopping all connections:', {
      connectionCount: this.activeConnections.size
    });
    
    logger.debug('Stopping all streaming connections', {
      component: 'ConnectionManager',
      connectionCount: this.activeConnections.size
    });

    for (const requestId of this.activeConnections.keys()) {
      this.cleanup(requestId);
    }
  }

  static isStreaming(requestId: string): boolean {
    const state = this.activeConnections.get(requestId);
    const streaming = state?.status === 'streaming' || state?.status === 'connecting';
    
    console.log('[CONNECTION MANAGER STREAM FIX] 📡 Streaming status check:', {
      requestId,
      status: state?.status,
      isStreaming: streaming
    });
    
    return streaming;
  }

  static getConnectionState(requestId: string): string | undefined {
    return this.activeConnections.get(requestId)?.status;
  }

  static getActiveConnectionsCount(): number {
    return this.activeConnections.size;
  }

  static getConnectionDetails(requestId?: string) {
    if (requestId) {
      const connection = this.activeConnections.get(requestId);
      return connection ? {
        status: connection.status,
        lastActivity: new Date(connection.lastActivity).toISOString(),
        startEventFired: connection.startEventFired
      } : null;
    }
    
    return Array.from(this.activeConnections.entries()).map(([id, conn]) => ({
      requestId: id,
      status: conn.status,
      lastActivity: new Date(conn.lastActivity).toISOString(),
      startEventFired: conn.startEventFired
    }));
  }
}
